import { SUPPORTED_LOCALES, type Locale } from "@dropl/shared";
import fr from "./locales/fr.json" with { type: "json" };
import en from "./locales/en.json" with { type: "json" };
import bg from "./locales/bg.json" with { type: "json" };
import cs from "./locales/cs.json" with { type: "json" };
import da from "./locales/da.json" with { type: "json" };
import de from "./locales/de.json" with { type: "json" };
import el from "./locales/el.json" with { type: "json" };
import es from "./locales/es.json" with { type: "json" };
import et from "./locales/et.json" with { type: "json" };
import fi from "./locales/fi.json" with { type: "json" };
import ga from "./locales/ga.json" with { type: "json" };
import hr from "./locales/hr.json" with { type: "json" };
import hu from "./locales/hu.json" with { type: "json" };
import it from "./locales/it.json" with { type: "json" };
import lt from "./locales/lt.json" with { type: "json" };
import lv from "./locales/lv.json" with { type: "json" };
import mt from "./locales/mt.json" with { type: "json" };
import nl from "./locales/nl.json" with { type: "json" };
import pl from "./locales/pl.json" with { type: "json" };
import pt from "./locales/pt.json" with { type: "json" };
import ro from "./locales/ro.json" with { type: "json" };
import sk from "./locales/sk.json" with { type: "json" };
import sl from "./locales/sl.json" with { type: "json" };
import sv from "./locales/sv.json" with { type: "json" };

type Dict = Record<string, unknown>;

const BUNDLES: Record<Locale, Dict> = {
  fr, en, bg, cs, da, de, el, es, et, fi,
  ga, hr, hu, it, lt, lv, mt, nl, pl, pt, ro, sk, sl, sv,
};
const STORAGE_KEY = "dropl:lang";

let currentLocale: Locale = "fr";
let fallbackLocale: Locale = "fr";
const listeners = new Set<(l: Locale) => void>();

function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function readStoredLocale(): Locale | undefined {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && isLocale(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

function readBrowserLocale(): Locale | undefined {
  for (const lang of navigator.languages ?? [navigator.language]) {
    const primary = lang.toLowerCase().split("-")[0];
    if (primary && isLocale(primary)) return primary;
  }
  return undefined;
}

function lookup(dict: Dict, key: string): string | undefined {
  let cursor: unknown = dict;
  for (const seg of key.split(".")) {
    if (cursor && typeof cursor === "object" && seg in (cursor as Dict)) {
      cursor = (cursor as Dict)[seg];
    } else {
      return undefined;
    }
  }
  return typeof cursor === "string" ? cursor : undefined;
}

function interpolate(tpl: string, params?: Record<string, string | number>): string {
  if (!params) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (_, n: string) => (n in params ? String(params[n]) : `{${n}}`));
}

function extractLocaleFromPath(path: string): { locale: Locale | null; rest: string } {
  const m = path.match(/^\/([a-z]{2})(?=\/|$)/);
  if (m && m[1]) {
    const candidate = m[1];

    if (isLocale(candidate)) {
      const rest = path.slice(3) || "/";
      return { locale: candidate, rest };
    }
  }
  return { locale: null, rest: path };
}

export function initI18n(defaultLocale: Locale): void {
  fallbackLocale = defaultLocale;
  const { locale: urlLocale, rest } = extractLocaleFromPath(location.pathname);
  if (urlLocale) {
    currentLocale = urlLocale;
    try { localStorage.setItem(STORAGE_KEY, urlLocale); } catch {  }
    const clean = rest + location.search + location.hash;
    history.replaceState({}, "", clean);
  } else {
    currentLocale = readStoredLocale() ?? readBrowserLocale() ?? defaultLocale;
  }
  document.documentElement.lang = currentLocale;
}

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(l: Locale): void {
  currentLocale = l;
  document.documentElement.lang = l;
  try { localStorage.setItem(STORAGE_KEY, l); } catch {  }
  for (const cb of listeners) cb(l);
}

export function onLocaleChange(cb: (l: Locale) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function t(key: string, params?: Record<string, string | number>): string {
  const raw =
    lookup(BUNDLES[currentLocale], key) ?? lookup(BUNDLES[fallbackLocale], key) ?? key;
  return interpolate(raw, params);
}
